import React, { useState, useEffect, useCallback } from "react";
import { fetchAll } from "../utils/fetchResources";
import { useHistory, Prompt } from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import { onReadyCKE, CKEconfig } from "../config/CKE";
import SetFile from "../components/SetFile";
import {
	Button,
	TextField,
	Typography,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Grid,
	Hidden,
	Card,
	CardContent,
	FormGroup,
	FormControlLabel,
	Switch,
	Input,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";

const fullName = (person) => {
	let name = "";
	if (person.firstName) {
		name += person.firstName;
	}
	if (person.middleName) {
		name += " " + person.middleName;
	}
	if (person.lastName) {
		name += " " + person.lastName;
	}
	if (person.suffix) {
		name += " " + person.suffix;
	}
	if (person.title) {
		name += ", " + person.title;
	}
	return name;
};

const useStyles = makeStyles((theme) => ({
	form: {
		width: "100%", // Fix IE 11 issue.
	},
	submit: {
		margin: theme.spacing(2, 0, 1),
	},
	images: {
		display: "flex",
		flexWrap: "wrap",
		maxHeight: "85vh",
		maxWidth: "85vw",
		overflowY: "auto",
	},
	gridImage: {
		"&::before": {
			content: "''",
			display: "block",
			paddingTop: "100%",
		},
		width: "33%!important",
		height: "auto!important",
		backgroundSize: "cover!important",
		backgroundPosition: "center center!important",
		backgroundRepeat: "no-repeat!important",
		cursor: "pointer!important",
	},
	selectedImage: {
		backgroundSize: "contain!important",
		backgroundPosition: "center center!important",
		backgroundRepeat: "no-repeat!important",
		height: "30vh",
		width: "100%",
	},
	CKElabels: {
		marginTop: "1.5rem",
		fontWeight: "bold",
	},
	info: {
		background: "#cce5ff",
		padding: ".5rem",
		textAlign: "center",
		borderRadius: "5px",
		margin: ".5rem 0",
	},
}));

export default function UnitsAdd(props) {
	const classes = useStyles();
	const [loading, setLoading] = useState(true);
	const [newUnit, setNewUnit] = useState({});
	const [units, setUnits] = useState([]);
	const [sites, setSites] = useState([]);
	const [people, setPeople] = useState([]);
	const [unsaved, setUnsaved] = useState(false);
	const [shortDescLength, setShortDescLength] = useState();
	const { enqueueSnackbar } = useSnackbar();
	const history = useHistory();

	const getPreexistingUnits = useCallback(
		async (site) => {
			try {
				var res;
				if (site && site !== "") {
					res = await fetch(
						`${process.env.REACT_APP_API_URL}/units?site=${site}`,
						{
							credentials: "include",
						}
					);
				} else {
					res = await fetch(`${process.env.REACT_APP_API_URL}/units`, {
						credentials: "include",
					});
				}
				const preexistingUnits = await res.json();
				if (preexistingUnits.success) {
					await setUnits(preexistingUnits.data);
				} else {
					enqueueSnackbar(preexistingUnits.error, { variant: "error" });
				}
			} catch (e) {
				console.log(e);
			}
		},
		[enqueueSnackbar]
	);

	useEffect(() => {
		(async () => {
			await getPreexistingUnits();
			await fetchAll("sites", setSites, "name");
			await fetchAll("people", setPeople, "lastName");
			setLoading(false);
		})();
	}, [getPreexistingUnits]);

	useEffect(() => {
		(async () => {
			await getPreexistingUnits(newUnit.site);
		})();
	}, [newUnit.site, getPreexistingUnits]);

	useEffect(() => {
		if (newUnit && newUnit.shortDescription) {
			setShortDescLength(newUnit.shortDescription.length);
		}
	}, [newUnit, setShortDescLength]);

	const onChange = (e) => {
		setNewUnit({ ...newUnit, [e.target.name]: e.target.value });
		setUnsaved(true);
	};

	const onChangeShortDesc = (e) => {
		setShortDescLength(e.target.value.length);
		setNewUnit({ ...newUnit, shortDescription: e.target.value });
	};

	const onChangeDescription = (e, editor) => {
		const data = editor.getData();
		setNewUnit({ ...newUnit, description: data });
		setUnsaved(true);
	};

	const onChangePublished = (e) => {
		setNewUnit({ ...newUnit, [e.target.name]: e.target.checked });
		setUnsaved(true);
	};

	const onSubmit = (e) => {
		e.preventDefault();
		fetch(`${process.env.REACT_APP_API_URL}/units`, {
			method: "POST",
			credentials: "include",
			body: JSON.stringify(newUnit),
			headers: { "Content-Type": "application/json" },
		})
			.then((res) => res.json())
			.then((data) => {
				if (data.success) {
					setUnsaved(false);
					enqueueSnackbar("Unit added successfully!", { variant: "success" });
					history.push(`/${process.env.REACT_APP_ADMIN_URL}/units`);
				} else {
					console.log(data);
					const errorArray = data.error.split(",");
					errorArray.forEach((error) => {
						enqueueSnackbar(`${error}`, { variant: "error" });
					});
				}
			});
	};

	if (loading) {
		return <div>Loading...</div>;
	}

	return (
		<>
			<Prompt
				when={unsaved}
				message="You have unsaved changes. Are you sure you want to leave?"
			/>
			<Typography component="h1" variant="h4">
				Add a Unit
			</Typography>
			<form className={classes.form} onSubmit={onSubmit}>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={12} md={9}>
						<TextField
							variant="outlined"
							margin="normal"
							fullWidth
							id="name"
							label="Unit Name"
							name="name"
							size="small"
							autoFocus
							required
							onChange={onChange}
						/>
						<Typography className={classes.info}>
							The following date field may be either the unit's start date or
							end date. It is only used for sorting and it will not show up
							anywhere on the public site.
						</Typography>
						<TextField
							variant="outlined"
							margin="normal"
							fullWidth
							id="sortDate"
							label="Unit Sort Date"
							name="sortDate"
							size="small"
							required
							onChange={onChange}
							type="date"
							InputLabelProps={{
								shrink: true,
							}}
						/>
						<FormControl
							fullWidth
							size="small"
							margin="normal"
							variant="outlined"
							className={classes.formControl}
							required
							name="site"
						>
							<InputLabel id="site-unit-label">Site</InputLabel>
							<Select
								inputProps={{ name: "site" }}
								value={newUnit.site ?? ""}
								onChange={onChange}
								label="site"
							>
								<MenuItem value="">
									<em>None</em>
								</MenuItem>
								{sites.map((site, index) => {
									return (
										<MenuItem key={index} value={site.id}>
											{site.name}
										</MenuItem>
									);
								})}
							</Select>
						</FormControl>
						{newUnit.site && (
							<FormControl
								fullWidth
								size="small"
								margin="normal"
								variant="outlined"
								className={classes.formControl}
							>
								<InputLabel id="parent-unit-label">Parent Unit</InputLabel>
								<Select
									inputProps={{ name: "parent" }}
									value={newUnit.parent ?? ""}
									onChange={onChange}
									label="Parent Unit"
								>
									<MenuItem value={null}>
										<em>None</em>
									</MenuItem>
									{units
										.filter((unit) => !unit.parent)
										.sort((a, b) => (a.name > b.name ? 1 : -1))
										.map((unit, index) => {
											return (
												<MenuItem key={index} value={unit.id}>
													{unit.name}
												</MenuItem>
											);
										})}
								</Select>
							</FormControl>
						)}
						<p className={classes.CKElabels} style={{ marginBottom: 0 }}>
							SHORT Description*:
							{shortDescLength !== undefined && shortDescLength <= 500 && (
								<span style={{ color: "green" }}>
									{" " + shortDescLength + " / 500"}
								</span>
							)}
							{shortDescLength !== undefined && shortDescLength > 500 && (
								<span style={{ color: "red" }}>
									{" " + shortDescLength + "/ 500"}
								</span>
							)}
						</p>
						<TextField
							multiline
							rows={4}
							variant="outlined"
							required
							id="shortDesc"
							label="Short description"
							name="shortDescription"
							onChange={onChangeShortDesc}
							size="small"
							margin="normal"
							fullWidth
							value={newUnit.shortDescription}
						></TextField>
						<p className={classes.CKElabels}>LONG Description*:</p>
						<CKEditor
							onReady={(editor) => {
								onReadyCKE(editor);
							}}
							editor={Editor}
							onChange={onChangeDescription}
							config={CKEconfig("Unit LONG Description...")}
						/>
						<Button
							type="submit"
							fullWidth
							variant="contained"
							color="primary"
							className={classes.submit}
						>
							Save
						</Button>
					</Grid>
					<Grid item xs={12} sm={12} md={3}>
						<Card>
							<CardContent>
								<Hidden smDown>
									<Button
										type="submit"
										fullWidth
										variant="contained"
										color="primary"
										className={classes.submit}
									>
										Save
									</Button>
								</Hidden>
								<FormGroup row>
									<FormControlLabel
										control={
											<Switch
												color="primary"
												checked={newUnit.published}
												onChange={onChangePublished}
												name="published"
											/>
										}
										label={
											newUnit.published ? (
												<Typography color="primary">Published</Typography>
											) : (
												<Typography color="secondary">Draft</Typography>
											)
										}
									/>
								</FormGroup>
								<SetFile
									fileTitle="Featured Image"
									resource={newUnit}
									setResource={setNewUnit}
									imageOnly={true}
									fileLabel="featuredImage"
									fileLabelFileName="featuredImageFile"
								/>
								<FormControl fullWidth size="medium">
									<InputLabel id="themes-input-label">People</InputLabel>
									<Select
										multiple
										name="people"
										value={newUnit.people ?? []}
										onChange={onChange}
										input={<Input id="people" />}
										renderValue={() => "View Selected"}
									>
										{people &&
											people.map((person) => (
												<MenuItem key={person.id} value={person._id}>
													{fullName(person)}
												</MenuItem>
											))}
									</Select>
								</FormControl>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</form>
		</>
	);
}
